import pino, {
  type DestinationStream,
  type Logger as PinoLogger,
  type LoggerOptions as PinoLoggerOptions,
} from "pino";

import type {
  OpenLogEvent,
  OpenLogFields,
  OpenLogger,
  OpenLogTimer,
  OpenLogValue,
} from "./types";

const DEBUG_LOGS_STORAGE_KEY = "open:debugLogs";
const DEBUG_LOGS_DISABLED_STORAGE_KEY = "open:debugLogsDisabled";
const DEBUG_LOGS_QUERY_PARAM = "debugLogs";
const DEBUG_LOGS_DISABLED_QUERY_PARAM = "debugLogsDisabled";
const DEBUG_LOGS_ENV_KEY = "OPEN_DEBUG_LOGS";
const DEBUG_LOGS_DISABLED_ENV_KEY = "OPEN_DEBUG_LOGS_DISABLED";
const PUBLIC_DEBUG_LOGS_ENV_KEY = "NEXT_PUBLIC_OPEN_DEBUG_LOGS";
const PUBLIC_DEBUG_LOGS_DISABLED_ENV_KEY =
  "NEXT_PUBLIC_OPEN_DEBUG_LOGS_DISABLED";
const DEFAULT_LOGGER_STATE_KEY = "__medlyOpenDefaultLoggerState";

// The "temporary" bucket is on by default in non-production builds so ad-hoc
// debug logs work without ceremony. Opt out with OPEN_DEBUG_LOGS_DISABLED.
export const TEMPORARY_DEBUG_FEATURE = "temporary";

const REDACTED_VALUE = "[Redacted]";

// Sensitive leaves redacted at any depth up to MAX_REDACT_NESTING. Pino's
// fast-redact pre-compiles these paths, so this is much cheaper than scanning
// every field with a regex.
const SENSITIVE_LEAVES = [
  "authorization",
  "Authorization",
  "cookie",
  "Cookie",
  "password",
  "secret",
  "token",
  "apiKey",
  "api_key",
  "prompt",
  "content",
  "body",
] as const;

const MAX_REDACT_NESTING = 3;

const REDACT_PATHS = buildRedactPaths(SENSITIVE_LEAVES, MAX_REDACT_NESTING);

interface CreateOpenLoggerOptions {
  readonly context?: OpenLogFields;
  readonly debugEnabled?: (feature?: string) => boolean;
  readonly destination?: DestinationStream;
  readonly environment?: string;
  readonly isProduction?: boolean;
  readonly mixin?: () => Record<string, unknown>;
  readonly now?: () => number;
  readonly pinoInstance?: PinoLogger;
  readonly version?: string;
}

interface OpenLoggerRuntime {
  readonly context: OpenLogFields;
  readonly debugEnabled: (feature?: string) => boolean;
  readonly isProduction: boolean;
  readonly now: () => number;
  readonly pino: PinoLogger;
}

interface DefaultLoggerState {
  instance: OpenLogger | null;
  options: CreateOpenLoggerOptions;
}

interface DefaultLoggerGlobal {
  [DEFAULT_LOGGER_STATE_KEY]?: DefaultLoggerState;
}

const noop = () => {};

export function createOpenLogger(
  options: CreateOpenLoggerOptions = {},
): OpenLogger {
  const isProduction = options.isProduction ?? isProductionEnvironment();
  const environment = options.environment ?? getEnvironment();
  const version = options.version ?? getVersion();
  const rootPino =
    options.pinoInstance ??
    createPinoInstance({
      destination: options.destination,
      environment,
      isProduction,
      mixin: options.mixin,
      version,
    });
  const context = options.context ?? {};
  const boundPino = hasFields(context)
    ? rootPino.child(toPinoBindings(context))
    : rootPino;

  return buildOpenLogger({
    context,
    debugEnabled: options.debugEnabled ?? isOpenDebugLogEnabled,
    isProduction,
    now: options.now ?? Date.now,
    pino: boundPino,
  });
}

export function normalizeOpenLogError(error: unknown): OpenLogValue {
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  if (error == null) {
    return { message: "Unknown error" };
  }

  return { message: String(error) };
}

function normalizeLogErrors(value: unknown): unknown {
  if (value instanceof Error) {
    return normalizeOpenLogError(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => normalizeLogErrors(entry));
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        normalizeLogErrors(entry),
      ]),
    );
  }

  return value;
}

export function isOpenDebugLogEnabled(feature?: string): boolean {
  if (isProductionEnvironment()) {
    return false;
  }

  const disabled = readDebugDisabledSetting();
  if (isFeatureInSetting(disabled, feature)) {
    return false;
  }

  if (feature === TEMPORARY_DEBUG_FEATURE) {
    return true;
  }

  return isFeatureInSetting(readDebugEnabledSetting(), feature);
}

function buildOpenLogger(runtime: OpenLoggerRuntime): OpenLogger {
  const writeInfo = createLevelWriter(runtime, "info");
  const writeWarn = createLevelWriter(runtime, "warn");
  const writeError = createLevelWriter(runtime, "error");
  const writeDebug = runtime.isProduction ? noop : createDebugWriter(runtime);

  return {
    debug: writeDebug,
    info: writeInfo,
    warn: writeWarn,
    error: writeError,
    child(childContext) {
      const childPino = runtime.pino.child(toPinoBindings(childContext));
      return buildOpenLogger({
        context: { ...runtime.context, ...childContext },
        debugEnabled: runtime.debugEnabled,
        isProduction: runtime.isProduction,
        now: runtime.now,
        pino: childPino,
      });
    },
    time(event, fields = {}) {
      return createTimer(runtime, event, fields);
    },
  };
}

function createLevelWriter(
  runtime: OpenLoggerRuntime,
  level: "info" | "warn" | "error",
): OpenLogger["info"] {
  return (event, fields = {}) => {
    runtime.pino[level](toLogObject(event, fields));
  };
}

function createDebugWriter(runtime: OpenLoggerRuntime): OpenLogger["debug"] {
  return (event, input = {}) => {
    const feature = getDebugFeature(event, runtime.context);
    if (!runtime.debugEnabled(feature)) {
      return;
    }

    const fields = typeof input === "function" ? input() : input;
    runtime.pino.debug(toLogObject(event, fields));
  };
}

function createTimer(
  runtime: OpenLoggerRuntime,
  event: OpenLogEvent,
  fields: OpenLogFields,
): OpenLogTimer {
  const startedAt = runtime.now();

  return {
    end(endFields = {}) {
      runtime.pino.info(
        toLogObject(event, {
          ...fields,
          ...endFields,
          durationMs: runtime.now() - startedAt,
          status: endFields.status ?? fields.status ?? "success",
        }),
      );
    },
    fail(error, failFields = {}) {
      runtime.pino.error(
        toLogObject(event, {
          ...fields,
          ...failFields,
          durationMs: runtime.now() - startedAt,
          error,
          status: failFields.status ?? "failure",
        }),
      );
    },
  };
}

interface CreatePinoOptions {
  readonly destination?: DestinationStream;
  readonly environment: string;
  readonly isProduction: boolean;
  readonly mixin?: () => Record<string, unknown>;
  readonly version: string;
}

function createPinoInstance(options: CreatePinoOptions): PinoLogger {
  const baseOptions: PinoLoggerOptions = {
    base: { environment: options.environment, version: options.version },
    browser: { asObject: true },
    formatters: {
      level(label) {
        return { level: label };
      },
    },
    level: options.isProduction ? "info" : "debug",
    ...(options.mixin ? { mixin: options.mixin } : {}),
    redact: { censor: REDACTED_VALUE, paths: REDACT_PATHS },
    serializers: {
      args: (value: unknown) => normalizeLogErrors(value),
      error: (value: unknown) => normalizeOpenLogError(value),
    },
    timestamp: pino.stdTimeFunctions.isoTime,
  };

  if (options.destination) {
    return pino(baseOptions, options.destination);
  }

  return pino(baseOptions);
}

function toLogObject(
  event: OpenLogEvent,
  fields: OpenLogFields,
): Record<string, unknown> {
  return { event, ...fields };
}

function toPinoBindings(fields: OpenLogFields): Record<string, unknown> {
  return { ...fields };
}

function hasFields(fields: OpenLogFields): boolean {
  for (const _key in fields) {
    return true;
  }
  return false;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === "[object Object]";
}

function getDebugFeature(
  event: OpenLogEvent,
  context: OpenLogFields,
): string | undefined {
  if (typeof context.feature === "string") {
    return context.feature;
  }
  const [, feature] = event.split(".");
  return feature || undefined;
}

function buildRedactPaths(
  leaves: readonly string[],
  maxDepth: number,
): string[] {
  const paths: string[] = [...leaves];
  for (let depth = 1; depth <= maxDepth; depth++) {
    const prefix = Array.from({ length: depth }, () => "*").join(".");
    for (const leaf of leaves) {
      paths.push(`${prefix}.${leaf}`);
    }
  }
  return paths;
}

function isFeatureInSetting(
  setting: string | undefined,
  feature?: string,
): boolean {
  if (!setting) {
    return false;
  }
  const tokens = setting
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (tokens.length === 0) {
    return false;
  }
  if (tokens.includes("*")) {
    return true;
  }
  return Boolean(feature && tokens.includes(feature));
}

function readDebugEnabledSetting(): string | undefined {
  return firstNonEmpty(
    readProcessEnv(DEBUG_LOGS_ENV_KEY),
    readProcessEnv(PUBLIC_DEBUG_LOGS_ENV_KEY),
    readBrowserSetting(DEBUG_LOGS_QUERY_PARAM, DEBUG_LOGS_STORAGE_KEY),
  );
}

function readDebugDisabledSetting(): string | undefined {
  return firstNonEmpty(
    readProcessEnv(DEBUG_LOGS_DISABLED_ENV_KEY),
    readProcessEnv(PUBLIC_DEBUG_LOGS_DISABLED_ENV_KEY),
    readBrowserSetting(
      DEBUG_LOGS_DISABLED_QUERY_PARAM,
      DEBUG_LOGS_DISABLED_STORAGE_KEY,
    ),
  );
}

function firstNonEmpty(
  ...values: readonly (string | undefined)[]
): string | undefined {
  return values.find((value) => Boolean(value?.trim()));
}

function readBrowserSetting(
  queryParam: string,
  storageKey: string,
): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return readQueryParam(queryParam) ?? readLocalStorage(storageKey);
}

function readQueryParam(name: string): string | undefined {
  try {
    return new URLSearchParams(window.location.search).get(name) ?? undefined;
  } catch {
    return undefined;
  }
}

function readLocalStorage(key: string): string | undefined {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function readProcessEnv(key: string): string | undefined {
  if (typeof process === "undefined") {
    return undefined;
  }
  return process.env?.[key];
}

function isProductionEnvironment(): boolean {
  return readProcessEnv("NODE_ENV") === "production";
}

function getEnvironment(): string {
  return (
    readProcessEnv("NEXT_PUBLIC_VERCEL_ENV") ??
    readProcessEnv("VERCEL_ENV") ??
    readProcessEnv("NODE_ENV") ??
    "development"
  );
}

function getVersion(): string {
  return (
    readProcessEnv("NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA") ??
    readProcessEnv("VERCEL_GIT_COMMIT_SHA") ??
    "development"
  );
}

// The default logger is built lazily and stored on globalThis rather than in
// module-local state. Turbopack can evaluate instrumentation and route bundles
// as separate module instances in the same Node process; globalThis keeps the
// server-configured sinks visible to every copy.
function getDefaultLoggerState(): DefaultLoggerState {
  const globalState = globalThis as DefaultLoggerGlobal;
  globalState[DEFAULT_LOGGER_STATE_KEY] ??= {
    instance: null,
    options: {},
  };
  return globalState[DEFAULT_LOGGER_STATE_KEY];
}

function getDefaultLogger(): OpenLogger {
  const state = getDefaultLoggerState();
  if (!state.instance) {
    state.instance = createOpenLogger(state.options);
  }
  return state.instance;
}

/**
 * Internal: replaces the options used to build the default `logger` singleton
 * and invalidates the cached instance. Intended to be called only from
 * `./server` (which is gated by `import "server-only"`), via
 * `instrumentation.ts`. Do not call from client code.
 */
export function configureDefaultLogger(options: CreateOpenLoggerOptions): void {
  const state = getDefaultLoggerState();
  state.options = options;
  state.instance = null;
}

export const logger: OpenLogger = {
  debug: (event, fields) => getDefaultLogger().debug(event, fields),
  info: (event, fields) => getDefaultLogger().info(event, fields),
  warn: (event, fields) => getDefaultLogger().warn(event, fields),
  error: (event, fields) => getDefaultLogger().error(event, fields),
  child: (context) => getDefaultLogger().child(context),
  time: (event, fields) => getDefaultLogger().time(event, fields),
};

export type {
  OpenDebugLogInput,
  OpenLogEvent,
  OpenLogFields,
  OpenLogger,
  OpenLogLevel,
  OpenLogTimer,
  OpenLogValue,
} from "./types";
